import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { MaskedLine, FadeUp } from "./Reveal";
import { useLightbox } from "./Lightbox";
import { BUSINESS, IMAGES } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STOCK_ITEMS = [
  { src: "/images/work-living-tv.webp", alt: "Walnut TV wall unit with designer partition by STAR AVG ENTERPRISE", caption: "TV unit & partition — our work", speed: 45, testId: "gallery-image-1", className: "aspect-[4/3] md:col-span-7" },
  { src: IMAGES.kitchen, alt: "Minimalist luxury modular kitchen", caption: "Modular kitchens", speed: 70, testId: "gallery-image-2", className: "aspect-[3/4] md:col-span-5 md:mt-32" },
  { src: "/images/recent-kitchen-build.jpg", video: "/videos/kitchen-build.mp4", poster: "/images/recent-kitchen-build.jpg", alt: "Recent modular kitchen built by STAR AVG ENTERPRISE", caption: "A recent build — our work, on video", speed: 60, testId: "gallery-video-1", className: "aspect-[3/4] md:col-span-5" },
  { src: IMAGES.closet, alt: "Spacious walk-in closet with custom storage", caption: "Wardrobes & walk-in storage", speed: 35, testId: "gallery-image-4", className: "aspect-[16/10] md:col-span-7 md:-mt-10" },
  { src: IMAGES.kitchenSleek, alt: "Sleek modular kitchen with contemporary cabinets", caption: "Kitchen craftsmanship", speed: 55, testId: "gallery-image-5", className: "aspect-[4/3] md:col-span-7" },
  { src: IMAGES.kitchenCopper, alt: "Elegant modern kitchen with copper accents", caption: "Island & breakfast counters", speed: 75, testId: "gallery-image-6", className: "aspect-[3/4] md:col-span-5 md:mt-24" },
  { src: IMAGES.kitchenMarble, alt: "Stylish kitchen with marble counter and pendant lamps", caption: "Details that matter", speed: 30, testId: "gallery-image-7", className: "aspect-[21/9] md:col-span-12" },
];

const ParallaxImage = ({ src, alt, caption, speed = 40, className = "", testId, video, poster, onOpen }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [-speed, speed]);
  return (
    <figure ref={ref} className={className}>
      <div
        className={`group overflow-hidden ${onOpen ? "cursor-zoom-in" : ""}`}
        onClick={onOpen}
        role={onOpen ? "button" : undefined}
        tabIndex={onOpen ? 0 : undefined}
        onKeyDown={onOpen ? (e) => e.key === "Enter" && onOpen() : undefined}
      >
        {video ? (
          <motion.video
            style={{ y }}
            poster={poster}
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            onCanPlay={(e) => e.target.play().catch(() => {})}
            data-testid={testId}
            aria-label={alt}
            className="h-full w-full scale-110 object-cover transition-transform duration-700 group-hover:scale-[1.18]"
          >
            <source src={video} type="video/mp4" />
            {video.replace(".mp4", ".webm") !== video && (
              <source src={video.replace(".mp4", ".webm")} type="video/webm" />
            )}
          </motion.video>
        ) : (
          <motion.img
            style={{ y }}
            src={src}
            alt={alt}
            data-testid={testId}
            className="h-full w-full scale-110 object-cover transition-transform duration-700 group-hover:scale-[1.18]"
          />
        )}
      </div>
      <figcaption className="mt-3 text-[10px] uppercase tracking-[0.3em] text-taupe">{caption}</figcaption>
    </figure>
  );
};

export default function Gallery() {
  const [photos, setPhotos] = useState([]);
  const { open } = useLightbox();
  const realItems = photos.map((p) => ({
    src: `${API}/photos/image/${p.id}`,
    alt: p.caption || p.category,
    caption: p.caption || p.category,
  }));

  useEffect(() => {
    axios
      .get(`${API}/photos`)
      .then((res) => setPhotos(res.data || []))
      .catch(() => {});
  }, []);

  return (
    <section id="gallery" data-testid="gallery-section" className="mx-auto max-w-[1400px] px-6 py-12 lg:px-12 lg:py-28">
      <div className="mb-6 grid grid-cols-1 gap-6 lg:mb-16 lg:grid-cols-12">
        <h2 className="font-serif text-4xl leading-[1.08] tracking-tighter sm:text-5xl lg:col-span-8 lg:text-6xl">
          <MaskedLine>The spaces we</MaskedLine>
          <MaskedLine delay={0.12}>
            <em className="text-terra">love to build.</em>
          </MaskedLine>
        </h2>
        <FadeUp delay={0.2} className="flex flex-col justify-end gap-6 lg:col-span-4">
          <p className="max-w-xs text-sm leading-relaxed text-taupe">
            Kitchens, wardrobes and living spaces — warm, practical and made to
            measure.
          </p>
          <a
            href={BUSINESS.photosLink}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="gallery-google-link"
            className="group inline-flex w-fit items-center gap-2 border-b border-ink pb-1 text-xs font-semibold uppercase tracking-[0.2em] transition-colors duration-300 hover:border-terra hover:text-terra"
          >
            All photos on our Google profile
            <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
        </FadeUp>
      </div>
      {photos.length > 0 && (
        <div className="mb-24" data-testid="real-projects-block">
          <FadeUp>
            <p className="mb-10 flex items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.35em] text-taupe">
              <span className="inline-block h-2 w-2 bg-clay" />
              Recent work by the studio
            </p>
          </FadeUp>
          <div className="grid grid-cols-1 gap-10 sm:grid-cols-2 lg:grid-cols-3">
            {photos.map((p, i) => (
              <FadeUp key={p.id} delay={i * 0.06}>
                <figure>
                  <div
                    className="group cursor-zoom-in overflow-hidden"
                    onClick={() => open(realItems, i)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => e.key === "Enter" && open(realItems, i)}
                  >
                    <img
                      src={`${API}/photos/image/${p.id}`}
                      alt={p.caption || "Project by STAR AVG ENTERPRISE"}
                      data-testid={`real-photo-${i + 1}`}
                      loading="lazy"
                      className="aspect-[4/3] w-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  </div>
                  <figcaption className="mt-3 flex items-center justify-between gap-4 text-[10px] uppercase tracking-[0.3em] text-taupe">
                    <span>{p.caption || "Recent project"}</span>
                    <span className="shrink-0 text-terra">{p.category}</span>
                  </figcaption>
                </figure>
              </FadeUp>
            ))}
          </div>
        </div>
      )}
      <div className="grid grid-cols-1 gap-6 md:grid-cols-12 md:gap-8">
        {STOCK_ITEMS.map((item, i) => (
          <ParallaxImage key={item.testId} {...item} onOpen={() => open(STOCK_ITEMS, i)} />
        ))}
      </div>
    </section>
  );
}
